import { Controller, useFormContext } from 'react-hook-form'
import {
  CurecallConfirmationModal,
  CurecallInput,
  CurecallMultiSelect,
  TOption,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import { TNodeFormValues } from '../node.type'
import { ELocale, ENABLED_PATIENT_LOCALES, PREFERED_LANGUAGE, TPatientLocale } from 'src/types/locales'
import { useChatbotFlow } from '../chatbot.provider'
import { ChatbotMessageTextarea } from '../../../_components/chatbot-message-textarea.component'

type TLanguageOption = TOption<TPatientLocale>

export function NodeTitleAndLanguages() {
  const { t } = useTranslation()
  const { updateCurrentNodeData } = useChatbotFlow()

  const {
    formState: { errors },
    control,
    getValues,
    setValue,
    watch,
  } = useFormContext<TNodeFormValues>()

  const LANGUAGE_OPTIONS: TLanguageOption[] = useMemo(
    () =>
      ENABLED_PATIENT_LOCALES.map((language) => ({
        label: t('common.languages', { language }),
        value: language,
      })),
    [t],
  )

  const messagesByLanguage = watch('messagesByLanguage')

  const selectedLanguageOptions = useMemo(() => {
    const selectedLanguages = Object.keys(messagesByLanguage) as TPatientLocale[]
    return selectedLanguages.length > 0
      ? LANGUAGE_OPTIONS.filter((opt) => selectedLanguages.includes(opt.value))
      : [
          LANGUAGE_OPTIONS.find((lang) => {
            return lang.value === PREFERED_LANGUAGE
          }) as TLanguageOption,
        ]
  }, [messagesByLanguage, LANGUAGE_OPTIONS])

  const [confirmLangToDelete, setConfirmLangToDelete] = useState<ELocale | undefined>(undefined)

  const handleChangeLanguages = (newOptions: TLanguageOption[]) => {
    const newSelectedLanguages = newOptions.map((opt) => opt.value)
    const removedLanguageOption = selectedLanguageOptions.find((opt) => !newOptions.includes(opt))
    const hasRemovedLanguageAMessage =
      removedLanguageOption && messagesByLanguage[removedLanguageOption.value]
    if (hasRemovedLanguageAMessage) {
      setConfirmLangToDelete(removedLanguageOption?.value)
      return
    }

    const updatedMessages: Partial<Record<TPatientLocale, string>> = {}
    for (const lang of newSelectedLanguages) {
      updatedMessages[lang] = messagesByLanguage[lang] || ''
    }

    setValue('messagesByLanguage', updatedMessages)
    updateCurrentNodeData({ messagesByLanguage: updatedMessages })
  }

  const onConfirmLanguageDeletion = () => {
    if (confirmLangToDelete) {
      const messagesUpdated = getValues('messagesByLanguage')
      delete messagesUpdated[confirmLangToDelete]

      setValue('messagesByLanguage', messagesUpdated)
      updateCurrentNodeData({ messagesByLanguage: messagesUpdated })
      setConfirmLangToDelete(undefined)
    }
  }

  const getSelectedLanguagesLabel = () => {
    return `${selectedLanguageOptions.length} ${t('pages.configuration.followups.chatbot.node-form.languages-selected')}`
  }

  return (
    <div className="c-flex c-flex-col c-gap-6">
      <div className="c-flex c-gap-4">
        <Controller
          control={control}
          name="title"
          rules={{ required: true }}
          render={({ field }) => (
            <CurecallInput
              label={t('pages.configuration.followups.chatbot.node-form.node-title')}
              placeholder={t('pages.configuration.followups.chatbot.node-form.node-title-placeholder')}
              value={field.value}
              onChange={(e) => {
                field.onChange(e)
                updateCurrentNodeData({ title: e.target.value })
              }}
              isRequired={true}
              errorMessage={errors.title && t('common.form.required')}
              tooltipContent={t('pages.configuration.followups.chatbot.node-form.node-title-tooltip')}
              tooltipPosition="right"
              wrapperClassName="c-w-full"
            />
          )}
        />

        <CurecallMultiSelect
          label={t('pages.configuration.followups.chatbot.node-form.node-languages')}
          items={LANGUAGE_OPTIONS}
          selectedItems={selectedLanguageOptions}
          onSelect={handleChangeLanguages}
          selectedElementsTriggerCount={3}
          selectedElementsLabel={getSelectedLanguagesLabel()}
          placeholder={t('pages.configuration.followups.chatbot.node-form.select-languages')}
          isRequired={true}
          wrapperClassName="c-w-full c-relative"
        />
      </div>

      <div className="c-flex c-flex-col c-gap-4">
        {selectedLanguageOptions.map(({ value: language }) => (
          <Controller
            key={language}
            control={control}
            name={`messagesByLanguage.${language}`}
            rules={{ required: true }}
            render={({ field }) => (
              <ChatbotMessageTextarea
                label={`${t('pages.configuration.followups.chatbot.node-form.message')} (${language.toUpperCase()})`}
                value={field.value || ''}
                onChange={(message) => {
                  field.onChange(message)
                  const currentMessages = getValues('messagesByLanguage')
                  updateCurrentNodeData({
                    messagesByLanguage: { ...currentMessages, [language]: message },
                  })
                }}
                isRequired={true}
                errorMessage={errors.messagesByLanguage?.[language] && t('common.form.required')}
              />
            )}
          />
        ))}
      </div>

      {confirmLangToDelete && (
        <CurecallConfirmationModal
          color="ALERT"
          title={t('pages.configuration.followups.chatbot.node-form.delete-language-title', {
            language:
              LANGUAGE_OPTIONS.find((opt) => opt.value === confirmLangToDelete)?.label || confirmLangToDelete,
          })}
          isOpen={true}
          cancelButtonLabel={t('common.actions.cancel')}
          confirmButtonLabel={t('common.actions.confirm')}
          onCancel={() => setConfirmLangToDelete(undefined)}
          onConfirm={() => onConfirmLanguageDeletion()}
        >
          {t('pages.configuration.followups.chatbot.node-form.delete-language-body')}

          <p className="c-bg-grey-50 c-mt-2 c-p-2 c-border-grey-300 c-border c-rounded-lg">
            {getValues('messagesByLanguage')[confirmLangToDelete] || ''}
          </p>

          {t('pages.configuration.followups.chatbot.node-form.confirm-deletion-message')}
        </CurecallConfirmationModal>
      )}
    </div>
  )
}
